/**
 * Settlement Viewer — Dev/Debug page for testing settlement generation.
 * Similar to CombatSimulator but for settlement layouts.
 */

import { useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { RefreshCw, Map, Building2, Trees, Castle, Home, Sparkles, Loader2 } from 'lucide-react';
import {
  generateSettlement,
  TIER_TEMPLATES,
  DISTRICT_COLORS,
  type Settlement,
  type SettlementTier,
  type SettlementConfig,
  type Building,
} from '@omen-quest/settlement-generator';
import { SettlementMapView } from '@/components/game-ui/SettlementMapView';

// ============================================================================
// TIER ICONS
// ============================================================================

const TIER_ICONS: Record<SettlementTier, React.ComponentType<{ size?: number; className?: string }>> = {
  hamlet: Home,
  village: Trees,
  town: Building2,
  city: Castle,
};

// ============================================================================
// COMPONENT
// ============================================================================

export function SettlementViewer() {
  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const [tier, setTier] = useState<SettlementTier>('town');
  const [seed, setSeed] = useState<number>(42);
  const [hasRiver, setHasRiver] = useState(true);
  const [isCoastal, setIsCoastal] = useState(false);
  const [, setSelectedBuilding] = useState<Building | null>(null);
  const [flavoring, setFlavoring] = useState(false);

  const generate = useCallback(() => {
    const config: SettlementConfig = {
      tier,
      seed,
      terrain: {
        terrain: 'plains',
        hasRiver,
        isCoastal,
        elevation: 'medium',
      },
    };
    const result = generateSettlement(config);
    setSettlement(result);
    setSelectedBuilding(null);
  }, [tier, seed, hasRiver, isCoastal]);

  const randomSeed = useCallback(() => {
    setSeed(Math.floor(Math.random() * 999999));
  }, []);

  const applyAIFlavoring = useCallback(async () => {
    if (!settlement || flavoring) return;
    setFlavoring(true);
    try {
      const res = await fetch('/api/settlements/flavor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          settlement,
          context: {
            region: 'roots',
            faction: 'ash_keepers',
            cultureTags: ['underground', 'secretive'],
            historyTags: ['pre_ebb_ruins'],
          },
        }),
      });
      if (!res.ok) throw new Error(`Flavoring failed: ${res.status}`);
      const flavored = await res.json();
      setSettlement(flavored);
    } catch (err) {
      console.error('Flavoring error:', err);
    } finally {
      setFlavoring(false);
    }
  }, [settlement, flavoring]);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 pb-8">
        <PageHeader title="Settlement Viewer" backHref="/admin" />

        {/* Controls */}
        <div className="flex flex-wrap gap-4 mb-4">
          {/* Tier selector */}
          <div className="flex gap-1 bg-white/5 rounded-lg p-1">
            {(['hamlet', 'village', 'town', 'city'] as SettlementTier[]).map(t => {
              const Icon = TIER_ICONS[t];
              return (
                <button
                  key={t}
                  onClick={() => setTier(t)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors',
                    tier === t ? 'bg-white/15 text-white' : 'text-neutral-400 hover:text-white',
                  )}
                >
                  <Icon size={14} />
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              );
            })}
          </div>

          {/* Seed input */}
          <div className="flex items-center gap-2">
            <label className="text-neutral-400 text-sm">Seed:</label>
            <input
              type="number"
              value={seed}
              onChange={e => setSeed(parseInt(e.target.value) || 0)}
              className="w-24 bg-white/5 border border-white/10 rounded px-2 py-1 text-sm text-white"
            />
            <button
              onClick={randomSeed}
              className="p-1.5 bg-white/5 border border-white/10 rounded hover:bg-white/10 transition-colors"
              title="Random seed"
            >
              <RefreshCw size={14} />
            </button>
          </div>

          {/* Terrain toggles */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-sm text-neutral-400 cursor-pointer">
              <input
                type="checkbox"
                checked={hasRiver}
                onChange={e => setHasRiver(e.target.checked)}
                className="accent-blue-500"
              />
              River
            </label>
            <label className="flex items-center gap-1.5 text-sm text-neutral-400 cursor-pointer">
              <input
                type="checkbox"
                checked={isCoastal}
                onChange={e => setIsCoastal(e.target.checked)}
                className="accent-blue-500"
              />
              Coastal
            </label>
          </div>

          {/* Generate button */}
          <button
            onClick={generate}
            className="flex items-center gap-2 px-4 py-1.5 bg-amber-700 hover:bg-amber-600 text-white rounded transition-colors text-sm font-medium"
          >
            <Map size={14} />
            Generate
          </button>

          {/* AI Flavor button */}
          {settlement && (
            <button
              onClick={applyAIFlavoring}
              disabled={flavoring}
              className={cn(
                'flex items-center gap-2 px-4 py-1.5 rounded transition-colors text-sm font-medium',
                flavoring
                  ? 'bg-purple-900/50 text-purple-300 cursor-wait'
                  : 'bg-purple-700 hover:bg-purple-600 text-white',
              )}
            >
              {flavoring ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {flavoring ? 'Flavoring...' : 'AI Flavor'}
            </button>
          )}
        </div>

        {/* Main content */}
        {settlement ? (
          <div className="flex gap-4">
            {/* Map */}
            <div className="flex-1">
              <SettlementMapView
                settlement={settlement}
                className="h-[600px] border border-white/10"
                onBuildingClick={setSelectedBuilding}
              />
            </div>

            {/* Sidebar: Stats */}
            <div className="w-72 space-y-4">
              {/* Settlement info */}
              <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                <h3 className="font-bold text-sm mb-2">
                  {settlement.flavoredName || settlement.placeholderName}
                </h3>
                <div className="space-y-1 text-xs">
                  <StatRow label="Tier" value={settlement.tier} />
                  <StatRow label="Seed" value={settlement.seed.toString()} />
                  <StatRow label="Districts" value={settlement.districts.length.toString()} />
                  <StatRow label="Buildings" value={settlement.buildings.length.toString()} />
                  <StatRow label="Roads" value={settlement.roads.length.toString()} />
                  <StatRow label="Walls" value={settlement.walls.length.toString()} />
                  <StatRow label="Gates" value={settlement.gates.length.toString()} />
                  <StatRow label="Water" value={settlement.waterFeatures.length.toString()} />
                  <StatRow label="Entries" value={settlement.entryPoints.length.toString()} />
                </div>
              </div>

              {/* District legend */}
              <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                <h3 className="font-bold text-sm mb-2">Districts</h3>
                <div className="space-y-1">
                  {settlement.districts.map(d => (
                    <div key={d.id} className="flex items-center gap-2 text-xs">
                      <div
                        className="w-3 h-3 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: DISTRICT_COLORS[d.type] }}
                      />
                      <span className="text-neutral-300 truncate">
                        {d.flavoredName || d.placeholderName}
                      </span>
                      <span className="text-neutral-500 ml-auto">{d.type}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Building categories */}
              <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                <h3 className="font-bold text-sm mb-2">Buildings by Category</h3>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {Object.entries(
                    settlement.buildings.reduce<Record<string, number>>((acc, b) => {
                      acc[b.category] = (acc[b.category] || 0) + 1;
                      return acc;
                    }, {})
                  )
                    .sort(([, a], [, b]) => b - a)
                    .map(([cat, count]) => (
                      <div key={cat} className="flex items-center justify-between text-xs">
                        <span className="text-neutral-300">{cat.replace(/_/g, ' ')}</span>
                        <span className="text-neutral-500">{count}</span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Tier template info */}
              <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                <h3 className="font-bold text-sm mb-2">Tier Template</h3>
                <div className="space-y-1 text-xs">
                  {(() => {
                    const tmpl = TIER_TEMPLATES[settlement.tier];
                    return (
                      <>
                        <StatRow label="Districts" value={`${tmpl.districtCount[0]}-${tmpl.districtCount[1]}`} />
                        <StatRow label="Buildings" value={`${tmpl.buildingCount[0]}-${tmpl.buildingCount[1]}`} />
                        <StatRow label="Walls" value={tmpl.hasWalls ? 'Yes' : 'No'} />
                        <StatRow label="Inner Wall" value={tmpl.hasInnerWall ? 'Yes' : 'No'} />
                        <StatRow label="Entry Roads" value={`${tmpl.entryRoads[0]}-${tmpl.entryRoads[1]}`} />
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-[600px] flex items-center justify-center border border-white/10 rounded-lg bg-white/5">
            <div className="text-center text-neutral-500">
              <Map size={48} className="mx-auto mb-3 opacity-30" />
              <p>Select a tier and click Generate to create a settlement</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-neutral-400">{label}</span>
      <span className="text-neutral-200 font-mono">{value}</span>
    </div>
  );
}

export default SettlementViewer;
